import React, { useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faCog,
  faSignOutAlt,
  faEnvelope,
  faComments,
  faMedkit,
  faLayerGroup,
  faList,
  faUserMd,
  faStethoscope,
  faCircleQuestion,
  faBars,
  faXmark,
  faBell,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { getAllNotificationCounts } from '../../services/firebase/notificationService';

const NAV_ITEMS = [
  { to: '/admin/dashboard', icon: faTachometerAlt, label: 'Dashboard', notifKey: null, group: 'overview' },
  { to: '/admin/appointments', icon: faList, label: 'Appointments', notifKey: 'pendingAppointments', group: 'overview' },
  { to: '/admin/treatments', icon: faStethoscope, label: 'Treatments', notifKey: null, group: 'manage' },
  { to: '/admin/services', icon: faMedkit, label: 'Services', notifKey: null, group: 'manage' },
  { to: '/admin/service-categories', icon: faLayerGroup, label: 'Service Categories', notifKey: null, group: 'manage' },
  { to: '/admin/doctors', icon: faUserMd, label: 'Doctors', notifKey: null, group: 'manage' },
  { to: '/admin/testimonials', icon: faComments, label: 'Testimonials', notifKey: 'pendingTestimonials', group: 'engage' },
  { to: '/admin/faqs', icon: faCircleQuestion, label: 'FAQs', notifKey: null, group: 'engage' },
  { to: '/admin/messages', icon: faEnvelope, label: 'Messages', notifKey: 'unreadMessages', group: 'engage' },
  { to: '/admin/settings', icon: faCog, label: 'Settings', notifKey: null, group: 'system' }
];

const GROUP_LABELS = {
  overview: 'Overview',
  manage: 'Manage Content',
  engage: 'Engagement',
  system: 'System'
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [adminUser, setAdminUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState({
    pendingAppointments: 0,
    unreadMessages: 0,
    pendingTestimonials: 0,
    total: 0
  });

  const getCurrentAdminUser = () => {
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    setAdminUser(getCurrentAdminUser());
    const handleStorageChange = (e) => {
      if (e.key === 'adminUser') setAdminUser(getCurrentAdminUser());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [location.pathname]);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (localStorage.getItem('adminLoggedIn') === 'true') {
        try {
          const counts = await getAllNotificationCounts();
          setNotifications(counts);
        } catch (error) {
          console.error('Error fetching notification counts:', error);
        }
      }
    };
    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [location.pathname]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminUser');
    setAdminUser(null);
    navigate('/admin/login');
  };

  const isLoginPage = location.pathname === '/admin/login';

  if (isLoginPage) {
    return (
      <main className="admin-login-main">
        <Outlet />
      </main>
    );
  }

  const isActive = (path) => location.pathname === path;

  const getInitials = (name) => {
    if (!name) return 'A';
    return name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const currentRoute = NAV_ITEMS.find((item) => isActive(item.to));
  const pageTitle = currentRoute ? currentRoute.label : 'Admin';

  const groupedNav = NAV_ITEMS.reduce((acc, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {});

  return (
    <div className="admin-shell">
      {sidebarOpen && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={`admin-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-brand">
          <div className="admin-brand-mark">
            <FontAwesomeIcon icon={faStethoscope} />
          </div>
          <div className="admin-brand-text">
            <span className="admin-brand-name">Life &amp; Wellness</span>
            <span className="admin-brand-sub">Admin Console</span>
          </div>
          <button
            type="button"
            className="admin-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <nav className="admin-nav">
          {Object.entries(groupedNav).map(([groupKey, items]) => (
            <div key={groupKey} className="admin-nav-group">
              <span className="admin-nav-group-title">{GROUP_LABELS[groupKey]}</span>
              {items.map((item) => {
                const active = isActive(item.to);
                const count = item.notifKey ? notifications[item.notifKey] || 0 : 0;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`admin-nav-link ${active ? 'is-active' : ''}`}
                  >
                    <span className="admin-nav-link-inner">
                      <FontAwesomeIcon icon={item.icon} className="admin-nav-icon" />
                      <span className="admin-nav-label">{item.label}</span>
                    </span>
                    {count > 0 && (
                      <span className={`admin-nav-badge admin-nav-badge--${item.notifKey}`}>
                        {count}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-card">
            <div className="admin-user-avatar">{getInitials(adminUser?.name)}</div>
            <div className="admin-user-meta">
              <span className="admin-user-name">{adminUser?.name || 'Admin'}</span>
              <span className="admin-user-role">{adminUser?.role || 'Administrator'}</span>
            </div>
          </div>
          <button type="button" className="admin-logout-btn" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" /> Sign out
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-left">
            <button
              type="button"
              className="admin-menu-toggle"
              onClick={() => setSidebarOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
            <div className="admin-breadcrumb">
              <span className="admin-breadcrumb-root">Console</span>
              <FontAwesomeIcon icon={faChevronRight} className="admin-breadcrumb-sep" />
              <span className="admin-breadcrumb-current">{pageTitle}</span>
            </div>
          </div>
          <div className="admin-topbar-right">
            <Link to="/admin/messages" className="admin-bell" aria-label="Notifications">
              <FontAwesomeIcon icon={faBell} />
              {notifications.total > 0 && (
                <Badge bg="danger" pill className="admin-bell-badge">{notifications.total}</Badge>
              )}
            </Link>
            <div className="admin-topbar-divider" aria-hidden="true" />
            <div className="admin-topbar-user">
              <span className="admin-topbar-greet">Welcome back,</span>
              <span className="admin-topbar-name">{adminUser?.name || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="admin-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
